import React from 'react';

/**
 * Top-level (app-wide) error boundary.
 *
 * ModuleErrorBoundary only wraps the lazy-loaded feature modules, so a render
 * error on a non-module route (login / admin / super-admin / portal / profile)
 * or in one of the top-level providers would white-screen the entire app.
 * This boundary wraps the whole tree (router + providers) so any such crash
 * shows a recoverable fallback instead of a blank page.
 */
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[App] Uncaught render error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleBackToLogin = () => {
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
          <div style={{ textAlign: 'center', maxWidth: '480px', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠</div>
            <h1 style={{ fontSize: '1.375rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              The application ran into an unexpected error. Reloading usually fixes it. If the
              problem persists, return to the login screen.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.5rem 1.25rem', fontSize: '0.85rem', fontWeight: 600,
                  background: '#111827', color: 'white', border: 'none',
                  borderRadius: '0.5rem', cursor: 'pointer'
                }}
              >
                Reload
              </button>
              <button
                onClick={this.handleBackToLogin}
                style={{
                  padding: '0.5rem 1.25rem', fontSize: '0.85rem', fontWeight: 600,
                  background: 'white', color: '#374151', border: '1px solid #d1d5db',
                  borderRadius: '0.5rem', cursor: 'pointer'
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;
